import React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import LoadingState from "../common/LoadingState";

function RequestCard({ title, meta, actionLabel, onAction, tone = "default" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        border: "1px solid #e3eaf3",
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.04)",
        p: 2.25,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, color: "#13233d" }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.55, color: "#60738c", lineHeight: 1.45 }}>
            {meta}
          </Typography>
        </Box>
        <Chip
          label={tone === "incoming" ? "Incoming" : "Pending"}
          size="small"
          sx={{
            bgcolor: tone === "incoming" ? "#fff1f2" : "#f4f7fb",
            color: tone === "incoming" ? "#b42318" : "#39516d",
            fontWeight: 700,
          }}
        />
      </Stack>
      {actionLabel ? (
        <Button variant={tone === "incoming" ? "contained" : "outlined"} onClick={onAction} sx={{ mt: 1.5 }}>
          {actionLabel}
        </Button>
      ) : null}
    </Paper>
  );
}

function getAccountabilityLabel(request) {
  const label = String(request?.mode_label || "").trim();
  if (!label || /challenge|sprint/i.test(label)) return "Accountability";
  return label;
}

function isChallengeRequest(request) {
  return request?.request_type === "challenge";
}

function getDurationLabel(request) {
  const days = Number(request?.duration_days || 7);
  return `${days} days`;
}

function getFocusLabel(request) {
  return String(request?.challenge_focus?.display_label || "General consistency").trim();
}

function getHabitsLabel(request) {
  const titles = Array.isArray(request?.challenge_focus?.habit_titles)
    ? request.challenge_focus.habit_titles.filter(Boolean)
    : [];
  if (!titles.length) return "";
  return ` Counts: ${titles.slice(0, 3).join(" · ")}${titles.length > 3 ? " · +more" : ""}.`;
}

export default function RequestsTab({ incoming = [], outgoing = [], loading, onRespond, onCancel }) {
  return (
    <Box sx={{ maxWidth: 1160 }}>
      <Box sx={{ mb: 3.5 }}>
        <Typography sx={{ fontSize: "2.2rem", lineHeight: 1.04, letterSpacing: 0, fontWeight: 850, color: "#102033" }}>
          Requests
        </Typography>
        <Typography sx={{ mt: 1, fontSize: "1.03rem", color: "#51657e", lineHeight: 1.45 }}>
          Keep up with incoming and outgoing TrustBud requests.
        </Typography>
      </Box>

      {loading ? (
        <LoadingState
          title="Checking requests"
          body="Looking for incoming and pending TrustBud requests."
          rows={2}
          compact
        />
      ) : incoming.length === 0 && outgoing.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e3eaf3", p: 3 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#13233d" }}>
            No requests right now
          </Typography>
          <Typography sx={{ mt: 0.75, color: "#60738c" }}>
            When someone reaches out, it will show up here.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.2}>
          {incoming.map((request) => (
            <RequestCard
              key={`incoming-${request.id}`}
              title={request?.from_user?.username || "TrustBud"}
              meta={
                isChallengeRequest(request)
                  ? `${request?.from_user?.username || "A TrustBud"} invited you to ${getAccountabilityLabel(request)} for ${getDurationLabel(request)}. Focus: ${getFocusLabel(request)}.${getHabitsLabel(request)}`
                  : `${request?.from_user?.username || "A TrustBud"} wants to add you as a TrustBud. Accept to add them to your people.`
              }
              actionLabel="Accept"
              tone="incoming"
              onAction={() => onRespond(request, "accepted")}
            />
          ))}
          {outgoing.map((request) => (
            <RequestCard
              key={`outgoing-${request.id}`}
              title={request?.to_user?.username || "TrustBud"}
              meta={
                isChallengeRequest(request)
                  ? `${getAccountabilityLabel(request)} for ${getDurationLabel(request)} waiting for a response. Focus: ${getFocusLabel(request)}.${getHabitsLabel(request)}`
                  : `TrustBud request waiting for ${request?.to_user?.username || "them"} to respond.`
              }
              actionLabel="Cancel"
              tone="outgoing"
              onAction={() => onCancel(request.id)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
